import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowDownLeft, Plus, Send,
  ArrowRight, Zap, ShieldCheck, Globe2, Wallet as WalletIcon,
  Bell, CheckCircle2, WifiOff,
} from "lucide-react";
import {
  AreaChart, Area,
  ResponsiveContainer, Tooltip, XAxis,
} from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CURRENCY_BY_CODE } from "@/lib/constants";
import { formatAmount } from "@/lib/format";
import { toast } from "sonner";
import { motion, AnimatePresence, ease as mmEase, Stagger, itemFadeUp, itemScaleIn } from "@/lib/motion";
import FCSBadge from "@/components/fcs/FCSBadge";

const ez = mmEase.expo;

const CURRENCY_COLORS = {
  GBP: "#10B981", USD: "#06B6D4", EUR: "#7C3AED",
  CAD: "#F59E0B", NGN: "#3B82F6", GHS: "#EF4444", KES: "#8B5CF6",
  RON: "#0EA5E9",
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20, filter: "blur(4px)" },
  animate: { opacity: 1, y: 0,  filter: "blur(0px)" },
  transition: { duration: 0.55, ease: ez, delay },
});

export default function Dashboard() {
  const { user } = useAuth();
  const [wallets,      setWallets]      = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [rates,        setRates]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    setFetchError(false);
    Promise.all([
      api.get("/wallets"),
      api.get("/transactions", { params: { limit: 10 } }),
      api.get("/fx/rates"),
    ]).then(([w, t, r]) => {
      setWallets(w.data);
      setTransactions(t.data);
      setRates(r.data);
    }).catch(() => {
      setFetchError(true);
      toast.error("Could not load your account data. Check your connection.");
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const ratesMap = useMemo(
    () => Object.fromEntries(rates.map((r) => [r.target, r.rate])),
    [rates],
  );

  const totalUsd = useMemo(() => {
    if (!wallets.length || !Object.keys(ratesMap).length) return 0;
    return wallets.reduce((acc, w) => {
      const r = ratesMap[w.currency];
      return r ? acc + w.balance / r : acc;
    }, 0);
  }, [wallets, ratesMap]);

  const series = useMemo(() => {
    const today = new Date();
    const days = Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - (6 - i));
      return {
        key:   d.toISOString().slice(0, 10),
        label: d.toLocaleDateString("en-GB", { weekday: "short" }),
        value: 0,
      };
    });
    const idx = Object.fromEntries(days.map((d, i) => [d.key, i]));
    for (const t of transactions) {
      if (t.direction === "send" && t.status === "completed") {
        const k = (t.created_at || "").slice(0, 10);
        if (k in idx && ratesMap[t.from_currency]) {
          days[idx[k]].value += t.send_amount / ratesMap[t.from_currency];
        }
      }
    }
    return days.map((d) => ({ name: d.label, value: Number(d.value.toFixed(2)) }));
  }, [transactions, ratesMap]);

  const weekTotal = useMemo(
    () => series.reduce((acc, d) => acc + d.value, 0),
    [series],
  );

  const weeklyCount = useMemo(
    () => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 7);
      return transactions.filter(
        (t) => t.direction === "send" && t.status === "completed" && new Date(t.created_at) > cutoff,
      ).length;
    },
    [transactions],
  );

  const recent = transactions.slice(0, 6);
  const firstName = user?.full_name?.split(" ")[0] || "there";

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="space-y-7">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0)} className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight leading-tight">
            {greeting()}, {firstName}.
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/app/receive"
            data-testid="dashboard-quick-receive"
            className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2.5 rounded-full border border-border hover:bg-secondary/60 text-navy transition-all"
          >
            <ArrowDownLeft size={13} /> Receive
          </Link>
          <Link
            to="/app/send"
            data-testid="dashboard-send-cta"
            className="btn-primary text-[13px] py-2.5 px-5"
          >
            <Send size={13} strokeWidth={2.2} />
            Send money
          </Link>
        </div>
      </motion.div>

      {/* ── Error state ─────────────────────────────────────────── */}
      {fetchError && !loading && (
        <motion.div
          {...fadeUp(0)}
          role="alert"
          data-testid="dashboard-error-state"
          className="card-elevated p-4 flex items-center justify-between gap-4"
          style={{ border: "1px solid rgba(239,68,68,0.2)", background: "rgba(239,68,68,0.04)" }}
        >
          <div className="flex items-center gap-3">
            <WifiOff size={17} className="shrink-0" style={{ color: "#EF4444" }} />
            <div>
              <p className="font-bold text-navy text-sm">Could not load account data</p>
              <p className="text-xs text-muted-foreground mt-0.5">Check your connection, then try again.</p>
            </div>
          </div>
          <button onClick={loadData} className="btn-ghost-dark shrink-0" data-testid="dashboard-retry">
            Retry
          </button>
        </motion.div>
      )}

      {/* ── Hero row ────────────────────────────────────────────── */}
      <motion.div
        className="grid lg:grid-cols-12 gap-4"
        {...fadeUp(0.06)}
      >
        {/* ── Balance card ──────────────────────────────────────── */}
        <div className="lg:col-span-7 brand-gradient rounded-3xl p-6 sm:p-8 relative overflow-hidden" data-testid="dashboard-balance-card">
          {/* Ambient orbs */}
          <div className="absolute -top-28 -right-28 w-96 h-96 rounded-full blur-3xl pointer-events-none"
            style={{ background: "rgba(16,185,129,0.18)" }} />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full blur-3xl pointer-events-none"
            style={{ background: "rgba(6,182,212,0.09)" }} />
          {/* Top-edge highlight */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          <div className="relative">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold">
                  Total balance · USD equivalent
                </p>
                <motion.p
                  data-testid="dashboard-total-balance"
                  className="font-display text-[3.2rem] sm:text-[3.8rem] font-black text-white num-tabular tracking-tight mt-1 leading-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.9, delay: 0.35 }}
                >
                  ${totalUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </motion.p>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-white/[0.08] border border-white/10 grid place-items-center">
                <WalletIcon size={18} className="text-white/60" />
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-white/40 font-medium mb-6">
              <span>{wallets.length} wallet{wallets.length !== 1 ? "s" : ""} active</span>
            </div>

            {/* Area chart */}
            <div className="h-28 -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ left: 0, right: 0, top: 2, bottom: 0 }}>
                  <defs>
                    <linearGradient id="emeraldFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#10B981" stopOpacity={0.40} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone" dataKey="value"
                    stroke="#10B981" strokeWidth={2}
                    fill="url(#emeraldFill)"
                    dot={false}
                    activeDot={{ r: 4, fill: "#10B981", strokeWidth: 0 }}
                  />
                  <XAxis
                    dataKey="name" axisLine={false} tickLine={false}
                    tick={{ fill: "rgba(255,255,255,0.38)", fontSize: 10, fontFamily: "'Inter', system-ui, sans-serif" }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(8,15,30,0.92)",
                      border: "1px solid rgba(255,255,255,0.09)",
                      borderRadius: 12,
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: 12,
                      color: "#fff",
                    }}
                    formatter={(v) => [`$${Number(v).toFixed(2)}`, "Sent"]}
                    cursor={{ stroke: "rgba(255,255,255,0.06)", strokeWidth: 1 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Quick actions */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/app/send"
                data-testid="dashboard-quick-send"
                className="btn-ghost text-sm py-2.5 px-5"
              >
                <ArrowUpRight size={13} /> Send
              </Link>
              <Link
                to="/app/receive"
                data-testid="dashboard-quick-receive"
                className="btn-ghost text-sm py-2.5 px-5"
              >
                <ArrowDownLeft size={13} /> Receive
              </Link>
              <Link
                to="/app/wallet"
                data-testid="dashboard-quick-topup"
                className="btn-ghost text-sm py-2.5 px-5"
              >
                <Plus size={13} /> Top up
              </Link>
            </div>
          </div>
        </div>

        {/* ── Right sidebar ─────────────────────────────────────── */}
        <div className="lg:col-span-5 flex flex-col gap-4">

          {/* KYC card */}
          <div className="card-elevated p-5 flex-1">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-2xl grid place-items-center ${
                  user?.kyc_status === "approved"
                    ? "bg-emerald/10"
                    : "bg-amber-50"
                }`}>
                  <ShieldCheck
                    size={18}
                    className={user?.kyc_status === "approved" ? "text-emerald" : "text-amber-500"}
                  />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Identity</p>
                  <p className="font-bold text-navy text-[13.5px] mt-0.5">KYC verification</p>
                </div>
              </div>
              <KycBadge status={user?.kyc_status} />
            </div>
            <p className="text-sm text-muted-foreground mt-3.5 leading-relaxed">
              {user?.kyc_status === "approved"
                ? "Fully verified. Send up to $50,000 / month."
                : "Verify your identity to unlock full transfer limits."}
            </p>
            <Link
              to="/app/kyc"
              data-testid="dashboard-kyc-link"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-emerald hover:gap-2.5 transition-all"
            >
              {user?.kyc_status === "approved" ? "View details" : "Complete now"}
              <ArrowRight size={13} />
            </Link>
          </div>

          {/* Quick send */}
          <div className="card-elevated p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-navy text-[13.5px]">Quick send</p>
              <div className="w-7 h-7 rounded-lg bg-amber-50 grid place-items-center">
                <Zap size={13} className="text-amber-500" />
              </div>
            </div>
            <div className="space-y-2">
              <SuggestionRow label="Family in Lagos"          from="GBP" to="NGN" testid="suggest-gbp-ngn" />
              <SuggestionRow label="Tuition in Accra"         from="USD" to="GHS" testid="suggest-usd-ghs" />
              <SuggestionRow label="Mobile money, Nairobi"   from="EUR" to="KES" testid="suggest-eur-kes" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Empty wallet nudge ──────────────────────────────────── */}
      {!loading && !fetchError && totalUsd === 0 && (
        <motion.div
          {...fadeUp(0.08)}
          data-testid="dashboard-topup-nudge"
          className="card-elevated p-5 flex items-center justify-between gap-4"
          style={{ border: "1px solid rgba(16,185,129,0.2)", background: "rgba(16,185,129,0.04)" }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald/10 grid place-items-center text-emerald shrink-0">
              <Plus size={18} strokeWidth={2} />
            </div>
            <div>
              <p className="font-bold text-navy text-sm">Add money to get started</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Top up your wallet to make your first transfer. It takes under a minute.
              </p>
            </div>
          </div>
          <Link
            to="/app/wallet"
            data-testid="dashboard-topup-nudge-cta"
            className="btn-primary shrink-0 text-sm py-2.5 px-5"
          >
            Top up <ArrowRight size={13} />
          </Link>
        </motion.div>
      )}

      {/* ── Wallet grid ──────────────────────────────────────────── */}
      <motion.section {...fadeUp(0.10)} data-testid="dashboard-wallets-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-black text-navy tracking-tight">Your wallets</h2>
          <Link
            to="/app/wallet"
            data-testid="dashboard-wallet-link"
            className="inline-flex items-center gap-1 text-sm text-emerald font-bold hover:gap-2 transition-all"
          >
            Manage <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" aria-busy={loading}>
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="skeleton h-28 rounded-2xl" aria-label="Loading wallet…" />
              ))
            : wallets.slice(0, 4).map((w, i) => (
                <WalletCard key={w.currency} wallet={w} index={i} />
              ))}
        </div>
      </motion.section>

      {/* ── Transfers + Chart ─────────────────────────────────────── */}
      <motion.div className="grid lg:grid-cols-12 gap-4" {...fadeUp(0.14)}>

        {/* Recent transfers */}
        <div className="lg:col-span-7 card-elevated overflow-hidden" data-testid="dashboard-recent-section">
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <div>
              <h2 className="font-display font-black text-navy tracking-tight">Recent transfers</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {transactions.length} total
              </p>
            </div>
            <Link
              to="/app/transactions"
              data-testid="dashboard-tx-link"
              className="text-sm text-emerald font-bold hover:underline"
            >
              See all
            </Link>
          </div>

          <div>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-6 py-3.5 border-t border-border/50">
                  <div className="skeleton w-9 h-9 rounded-xl" />
                  <div className="flex-1 space-y-1.5">
                    <div className="skeleton h-3 w-32 rounded" />
                    <div className="skeleton h-2.5 w-20 rounded" />
                  </div>
                  <div className="skeleton h-3 w-16 rounded" />
                </div>
              ))
            ) : recent.length === 0 ? (
              <div className="py-14 text-center">
                <div className="w-14 h-14 rounded-2xl bg-secondary/80 grid place-items-center mx-auto mb-3">
                  <Globe2 size={24} className="text-muted-foreground/50" />
                </div>
                <p className="text-[13px] text-muted-foreground">
                  No transfers yet — your first send is one tap away.
                </p>
              </div>
            ) : (
              recent.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.32, ease: ez, delay: i * 0.04 }}
                >
                  <TxRow tx={t} />
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* This week */}
        <div className="lg:col-span-5 card-elevated p-6 flex flex-col justify-between">
          <div>
            <h2 className="font-display font-black text-navy tracking-tight mb-0.5">This week</h2>
            <p className="text-[11px] text-muted-foreground mb-5">Last 7 days · USD normalised</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-secondary/70 rounded-2xl p-4">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">
                Total sent
              </p>
              <p className="font-display font-black text-[1.2rem] text-navy num-tabular mt-1 tracking-tight">
                {weekTotal > 0 ? `$${weekTotal.toFixed(0)}` : "—"}
              </p>
            </div>
            <div className="bg-secondary/70 rounded-2xl p-4">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">
                Transfers
              </p>
              <p className="font-display font-black text-[1.2rem] text-navy num-tabular mt-1 tracking-tight">
                {weeklyCount > 0 ? weeklyCount : "—"}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Wallet card ─────────────────────────────────────────────────── */
function WalletCard({ wallet, index }) {
  const cur   = CURRENCY_BY_CODE[wallet.currency] || {};
  const color = CURRENCY_COLORS[wallet.currency]  || "#10B981";

  return (
    <motion.div
      className="card-float p-4 relative overflow-hidden group cursor-pointer"
      initial={{ opacity: 0, scale: 0.94, y: 14 }}
      animate={{ opacity: 1, scale: 1,    y: 0 }}
      transition={{ duration: 0.45, ease: ez, delay: 0.08 + index * 0.06 }}
      whileHover={{ y: -4 }}
    >
      {/* Colored ambient glow */}
      <div
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-20 group-hover:opacity-35 transition-opacity duration-500"
        style={{ background: color }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xl">{cur.flag || "💰"}</span>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${color}18`, color }}
          >
            {wallet.currency}
          </span>
        </div>
        <p className="font-display text-[1.3rem] font-black text-navy num-tabular tracking-tight">
          {formatAmount(wallet.balance, wallet.currency)}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{cur.name || wallet.currency}</p>
      </div>
    </motion.div>
  );
}

/* ── Transaction row ──────────────────────────────────────────────── */
const TX_STATUS_VARIANT = {
  completed:  "success",
  pending:    "pending",
  processing: "processing",
  failed:     "failure",
};

function TxRow({ tx }) {
  const isSend  = tx.direction === "send";
  const fromCur = CURRENCY_BY_CODE[tx.from_currency] || {};
  const toCur   = CURRENCY_BY_CODE[tx.to_currency]   || {};

  const fcsVariant = TX_STATUS_VARIANT[tx.status] || "pending";
  const statusLabel = {
    completed:  isSend ? "Sent" : "Received",
    pending:    "Pending",
    processing: "Processing",
    failed:     "Failed",
  }[tx.status] || tx.status;

  return (
    <Link
      to={`/app/transactions/${tx.id}`}
      className="flex items-center gap-3 px-6 py-3.5 border-t border-border/50 hover:bg-secondary/40 transition-colors group"
    >
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
        isSend ? "bg-coral/[0.09] text-coral" : "bg-emerald/10 text-emerald"
      }`}>
        {isSend
          ? <ArrowUpRight  size={15} strokeWidth={2.2} />
          : <ArrowDownLeft size={15} strokeWidth={2.2} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-navy truncate">
          {isSend
            ? `${fromCur.flag || ""} Sent to ${toCur.flag || ""}`
            : `${fromCur.flag || ""} Received`}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {tx.created_at
            ? new Date(tx.created_at).toLocaleDateString("en-GB", {
                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
              })
            : "—"}
        </p>
      </div>
      <div className="text-right shrink-0 flex flex-col items-end gap-1">
        <p className={`text-[13px] font-bold num-tabular ${isSend ? "text-navy" : "text-emerald"}`}>
          {isSend ? "−" : "+"}
          {formatAmount(tx.send_amount || 0, tx.from_currency)}
        </p>
        <FCSBadge variant={fcsVariant} label={statusLabel} />
      </div>
    </Link>
  );
}

/* ── KYC badge ────────────────────────────────────────────────────── */
function KycBadge({ status }) {
  const m = {
    approved:   { variant: "success",    label: "Verified"      },
    pending:    { variant: "pending",    label: "In review"     },
    rejected:   { variant: "failure",    label: "Rejected"      },
    unverified: { variant: "warning",    label: "Action needed" },
  };
  const { variant, label } = m[status || "unverified"];
  return <FCSBadge variant={variant} label={label} />;
}

/* ── Quick suggestion row ─────────────────────────────────────────── */
function SuggestionRow({ label, from, to, testid }) {
  const f = CURRENCY_BY_CODE[from];
  const t = CURRENCY_BY_CODE[to];

  return (
    <Link
      to={`/app/send?from=${from}&to=${to}`}
      data-testid={testid}
      className="flex items-center justify-between p-3 rounded-xl bg-secondary/60 hover:bg-secondary transition-colors group"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex items-center -space-x-1.5">
          <span className="text-[15px]">{f?.flag}</span>
          <span className="text-[15px]">{t?.flag}</span>
        </div>
        <div>
          <p className="text-[12px] font-semibold text-navy">{label}</p>
          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
            {from} → {to}
          </p>
        </div>
      </div>
      <ArrowRight
        size={13}
        className="text-emerald group-hover:translate-x-0.5 transition-transform"
      />
    </Link>
  );
}
